import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { identifyUser } from '@/lib/analytics'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCustomToken,
  sendPasswordResetEmail,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth'
import { doc, onSnapshot, terminate, clearIndexedDbPersistence } from 'firebase/firestore'
import { auth, db, googleProvider, appleProvider } from '@/lib/firebase'
import { fetchWebAccess } from '@/billing/access'
import type { UserProfile } from '@/types'

/**
 * Web access state:
 *  - 'checking'  → still resolving (auth/profile/server verify in flight)
 *  - 'granted'   → premium or trial; the app is unlocked
 *  - 'denied'    → no active subscription; route to the upgrade gate
 */
export type AccessState = 'checking' | 'granted' | 'denied'

interface AuthContextValue {
  user: User | null
  userProfile: UserProfile | null
  /** Signed in, profile load finished, and no lb_users doc exists. */
  profileMissing: boolean
  /** True until initial auth (and any SSO token sign-in) resolves. */
  loading: boolean
  profileLoading: boolean
  /** Raw mirror of lb_users/{uid}.isPremium (instant, may be briefly stale). */
  isPremium: boolean
  /** Authoritative access decision (mirror + server verify). */
  access: AccessState
  /** Re-run the server access check (e.g. after a successful checkout). */
  recheckAccess: () => Promise<void>
  signInEmail: (email: string, password: string) => Promise<void>
  signInGoogle: () => Promise<void>
  signInApple: () => Promise<void>
  signInToken: (token: string) => Promise<void>
  resetPassword: (email: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function hasSsoToken(): boolean {
  return new URLSearchParams(window.location.search).has('token')
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  // Set to the uid once the profile subscription has delivered a SERVER-
  // confirmed answer for that user. profileMissing must never be derived from
  // effect-ordering races or cached misses (audit #2/#7): a cold load renders
  // one commit with user set but the profile effect not yet run.
  const [profileResolvedUid, setProfileResolvedUid] = useState<string | null>(null)
  const [serverAccess, setServerAccess] = useState<boolean | null>(null)
  const [accessChecking, setAccessChecking] = useState(false)
  const [accessChecked, setAccessChecked] = useState(false)
  // If we arrived via an app→web SSO link, hold "loading" until sign-in resolves
  // so we don't flash the sign-in screen.
  const [ssoDone, setSsoDone] = useState(() => !hasSsoToken())

  // App → web SSO: ?token=<custom token from createAccountPortalToken>.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    if (!token) return
    signInWithCustomToken(auth, token)
      .catch(() => {
        /* invalid/expired token — fall through to normal sign-in */
      })
      .finally(() => {
        params.delete('token')
        const qs = params.toString()
        const url =
          window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash
        window.history.replaceState({}, '', url)
        setSsoDone(true)
      })
  }, [])

  // Auth state.
  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u)
      identifyUser(u ? u.uid : null)
      setAuthLoading(false)
      if (!u) {
        setUserProfile(null)
        setServerAccess(null)
        setProfileResolvedUid(null)
      }
    })
  }, [])

  // Real-time user profile (mirrors mobile AuthContext.subscribeToUserProfile).
  useEffect(() => {
    if (!user) return
    const uid = user.uid
    setProfileLoading(true)
    setProfileResolvedUid(null)
    const ref = doc(db, 'lb_users', uid)
    const unsub = onSnapshot(
      ref,
      { includeMetadataChanges: true },
      (snap) => {
        if (snap.exists()) {
          setUserProfile({ id: snap.id, ...snap.data() } as UserProfile)
          setProfileResolvedUid(uid)
        } else if (!snap.metadata.fromCache) {
          // Server-confirmed: this account really has no profile.
          setUserProfile(null)
          setProfileResolvedUid(uid)
        }
        // Cached miss → not authoritative; keep waiting for the server result.
        setProfileLoading(false)
      },
      (err) => {
        // Listener error (offline/rules blip): never conclude "no account".
        console.error('Profile subscription failed:', err)
        setProfileLoading(false)
      },
    )
    return unsub
  }, [user])

  // Authoritative access verify (runs once per auth session).
  useEffect(() => {
    if (!user) return
    let cancelled = false
    setAccessChecking(true)
    setAccessChecked(false)
    fetchWebAccess()
      .then((ok) => {
        if (!cancelled) setServerAccess(ok)
      })
      .catch(() => {
        if (!cancelled) setServerAccess(null) // unknown → fall back to mirror
      })
      .finally(() => {
        if (!cancelled) {
          setAccessChecking(false)
          setAccessChecked(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [user])

  const recheckAccess = useCallback(async () => {
    if (!auth.currentUser) return
    setAccessChecking(true)
    try {
      setServerAccess(await fetchWebAccess())
    } catch {
      /* keep previous value */
    } finally {
      setAccessChecking(false)
    }
  }, [])

  // Signed in but no lb_users doc: a fresh Google/Apple sign-in that never
  // onboarded in the app (the mobile "orphan account" shape). These users must
  // finish setup in the app — they should never see the paywall or the app UI.
  // Only ever true after a server-confirmed miss for THIS uid.
  const profileMissing = !!user && profileResolvedUid === user.uid && userProfile === null

  const hasMirror = userProfile?.isPremium === true

  // Late-webhook auto-unlock: if the real-time profile push flips isPremium
  // true while the last server check said denied, re-verify — otherwise a paid
  // user sits on the gate until they click refresh (audit #4).
  useEffect(() => {
    if (hasMirror && serverAccess === false && !accessChecking) void recheckAccess()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire on mirror flips only
  }, [hasMirror])

  // DEV-ONLY: bypass the premium gate for local testing. `import.meta.env.DEV` is
  // false in any production build, so this path is compiled out and can never ship.
  const devBypass = import.meta.env.DEV && import.meta.env.VITE_BYPASS_GATE === '1'

  let access: AccessState
  if (devBypass) access = 'granted'
  else if (serverAccess === true) access = 'granted'
  else if (serverAccess === false)
    access = 'denied' // server is authoritative — overrides a stale-true mirror
  else if (hasMirror) access = 'granted' // server unknown yet → trust mirror provisionally
  // Not yet resolved (first load): stay 'checking' so a paying user never flashes the gate.
  else if (!accessChecked || accessChecking || profileLoading) access = 'checking'
  else access = 'denied'

  const value: AuthContextValue = {
    user,
    userProfile,
    profileMissing,
    loading: authLoading || !ssoDone,
    profileLoading,
    isPremium: hasMirror,
    access,
    recheckAccess,
    signInEmail: async (email, password) => {
      await signInWithEmailAndPassword(auth, email, password)
    },
    signInGoogle: async () => {
      await signInWithPopup(auth, googleProvider)
    },
    signInApple: async () => {
      await signInWithPopup(auth, appleProvider)
    },
    signInToken: async (token) => {
      await signInWithCustomToken(auth, token)
    },
    resetPassword: async (email) => {
      await sendPasswordResetEmail(auth, email)
    },
    signOut: async () => {
      await fbSignOut(auth)
      // Shared-computer hygiene: the persistent cache holds health data on
      // disk. Terminate + clear, then hard-reload into a clean session. If
      // another tab holds the DB the clear throws — data stays uid-scoped
      // and unreadable in-app, so signing out still succeeds.
      try {
        await terminate(db)
        await clearIndexedDbPersistence(db)
      } catch {
        /* multi-tab or already terminated */
      }
      window.location.replace('/sign-in')
    },
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
